import { jsx as _jsx } from "react/jsx-runtime";
import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStageViewport } from '@/hooks/board';
import { useBoardHistory } from '@/hooks/useHistory';
import { stageRefAtom } from '@/store/boardAtoms';
import { getViewportBounds } from '@/utils/board/viewportUtils';
/**
 * Миникарта доски - показывает обзор всех элементов и текущий viewport.
 * Появляется при перемещении вида и исчезает через 3 секунды.
 * При наведении курсора не исчезает.
 * При клике переводит вид на выбранную точку.
 */
const MiniMap = ({ styles }) => {
    const { boardState } = useBoardHistory();
    const { viewport, setPosition } = useStageViewport();
    const { zoom, position: stagePosition } = viewport;
    const [stageRef] = useAtom(stageRefAtom);
    const [isVisible, setIsVisible] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const timeoutRef = useRef(null);
    const previousPositionRef = useRef(stagePosition);
    const previousZoomRef = useRef(zoom);
    const containerRef = useRef(null);
    const canvasRef = useRef(null);
    // Константы размеров миникарты
    const MINIMAP_WIDTH = 200;
    const MINIMAP_HEIGHT = 150;
    const PADDING = 10;
    const HIDE_DELAY = 1000; // Задержка скрытия в мс
    const POSITION_THRESHOLD = 1; // Порог изменения позиции для показа миникарты
    const ZOOM_THRESHOLD = 0.001; // Порог изменения зума для показа миникарты
    /**
     * Вычисляет границы всех видимых элементов доски
     * Используется для определения масштаба миникарты
     */
    const boardBounds = useMemo(() => {
        var _a, _b, _c, _d;
        const elements = [];
        // Собираем все видимые элементы из видимых слоев
        for (const layer of boardState.layers || []) {
            if (layer.visible) {
                elements.push(...(layer.elements || []));
            }
        }
        // Если элементов нет, возвращаем дефолтные границы
        if (elements.length === 0) {
            return {
                minX: -1000,
                minY: -1000,
                maxX: 1000,
                maxY: 1000,
                width: 2000,
                height: 2000,
            };
        }
        // Находим границы всех элементов
        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;
        for (const element of elements) {
            const x = ((_a = element.props) === null || _a === void 0 ? void 0 : _a.x) || 0;
            const y = ((_b = element.props) === null || _b === void 0 ? void 0 : _b.y) || 0;
            const w = ((_c = element.props) === null || _c === void 0 ? void 0 : _c.w) || 64;
            const h = ((_d = element.props) === null || _d === void 0 ? void 0 : _d.h) || 64;
            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
            maxX = Math.max(maxX, x + w);
            maxY = Math.max(maxY, y + h);
        }
        // Добавляем отступы для лучшей видимости
        const padding = 200;
        return {
            minX: minX - padding,
            minY: minY - padding,
            maxX: maxX + padding,
            maxY: maxY + padding,
            width: maxX - minX + padding * 2,
            height: maxY - minY + padding * 2,
        };
    }, [boardState.layers]);
    /**
     * Вычисляет масштаб для отображения доски на миникарте
     */
    const minimapScale = useMemo(() => {
        const scaleX = (MINIMAP_WIDTH - PADDING * 2) / boardBounds.width;
        const scaleY = (MINIMAP_HEIGHT - PADDING * 2) / boardBounds.height;
        return Math.min(scaleX, scaleY);
    }, [boardBounds]);
    /**
     * Преобразует мировые координаты в координаты миникарты
     */
    const worldToMinimap = useCallback((worldX, worldY) => {
        const x = PADDING + (worldX - boardBounds.minX) * minimapScale;
        const y = PADDING + (worldY - boardBounds.minY) * minimapScale;
        return { x, y };
    }, [boardBounds, minimapScale]);
    /**
     * Преобразует координаты миникарты в мировые координаты
     */
    const minimapToWorld = useCallback((minimapX, minimapY) => {
        const worldX = boardBounds.minX + (minimapX - PADDING) / minimapScale;
        const worldY = boardBounds.minY + (minimapY - PADDING) / minimapScale;
        return { x: worldX, y: worldY };
    }, [boardBounds, minimapScale]);
    /**
     * Отрисовывает миникарту на canvas
     * Рисует элементы доски и текущий viewport
     */
    const drawMinimap = useCallback(() => {
        var _a, _b, _c, _d;
        const canvas = canvasRef.current;
        const ctx = canvas === null || canvas === void 0 ? void 0 : canvas.getContext('2d');
        if (!ctx)
            return;
        // Очищаем и заливаем фон
        ctx.clearRect(0, 0, MINIMAP_WIDTH, MINIMAP_HEIGHT);
        ctx.fillStyle = 'rgba(24, 27, 28, 0.9)';
        ctx.fillRect(0, 0, MINIMAP_WIDTH, MINIMAP_HEIGHT);
        // Настройки для отрисовки элементов
        ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
        ctx.lineWidth = 1;
        // Отрисовываем все видимые элементы
        for (const layer of boardState.layers || []) {
            if (!layer.visible)
                continue;
            for (const element of layer.elements || []) {
                const x = ((_a = element.props) === null || _a === void 0 ? void 0 : _a.x) || 0;
                const y = ((_b = element.props) === null || _b === void 0 ? void 0 : _b.y) || 0;
                const w = ((_c = element.props) === null || _c === void 0 ? void 0 : _c.w) || 64;
                const h = ((_d = element.props) === null || _d === void 0 ? void 0 : _d.h) || 64;
                const minimapPos = worldToMinimap(x, y);
                const minimapW = w * minimapScale;
                const minimapH = h * minimapScale;
                // Пропускаем слишком маленькие элементы для оптимизации
                if (minimapW < 1 && minimapH < 1)
                    continue;
                ctx.fillRect(minimapPos.x, minimapPos.y, minimapW, minimapH);
                ctx.strokeRect(minimapPos.x, minimapPos.y, minimapW, minimapH);
            }
        }
        // Отрисовываем текущий viewport (область видимости)
        const viewportBounds = getViewportBounds(stagePosition, zoom, window.innerWidth, window.innerHeight);
        const viewportTopLeft = worldToMinimap(viewportBounds.left, viewportBounds.top);
        const viewportBottomRight = worldToMinimap(viewportBounds.right, viewportBounds.bottom);
        const viewportX = viewportTopLeft.x;
        const viewportY = viewportTopLeft.y;
        const viewportW = viewportBottomRight.x - viewportTopLeft.x;
        const viewportH = viewportBottomRight.y - viewportTopLeft.y;
        // Яркая рамка viewport для лучшей видимости
        ctx.strokeStyle = 'rgba(113, 138, 149, 1)';
        ctx.fillStyle = 'rgba(113, 138, 149, 0.3)';
        ctx.lineWidth = 2;
        ctx.fillRect(viewportX, viewportY, viewportW, viewportH);
        ctx.strokeRect(viewportX, viewportY, viewportW, viewportH);
    }, [boardState.layers, stagePosition, zoom, worldToMinimap, minimapScale]);
    /**
     * Очищает таймер скрытия миникарты
     */
    const clearHideTimeout = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);
    /**
     * Запускает таймер скрытия миникарты
     */
    const startHideTimeout = useCallback(() => {
        clearHideTimeout();
        timeoutRef.current = setTimeout(() => {
            if (!isHovered) {
                setIsVisible(false);
            }
            timeoutRef.current = null;
        }, HIDE_DELAY);
    }, [isHovered, clearHideTimeout]);
    // Throttle для оптимизации частых обновлений при панорамировании
    const minimapThrottleRef = useRef(null);
    const lastMinimapDrawRef = useRef(0);
    const MINIMAP_THROTTLE_MS = 100; // Миникарта не требует высокой частоты обновления
    // Отслеживаем изменения позиции и зума для показа миникарты
    useEffect(() => {
        const positionChanged = Math.abs(previousPositionRef.current.x - stagePosition.x) > POSITION_THRESHOLD ||
            Math.abs(previousPositionRef.current.y - stagePosition.y) > POSITION_THRESHOLD;
        const zoomChanged = Math.abs(previousZoomRef.current - zoom) > ZOOM_THRESHOLD;
        const zoomLevelActive = zoom <= 0.5;
        if ((positionChanged || zoomChanged) && zoomLevelActive) {
            setIsVisible(true);
            previousPositionRef.current = stagePosition;
            previousZoomRef.current = zoom;
            // Throttle перерисовку миникарты для оптимизации производительности
            const now = performance.now();
            const timeSinceLastDraw = now - lastMinimapDrawRef.current;
            if (timeSinceLastDraw >= MINIMAP_THROTTLE_MS) {
                // Рисуем сразу, если прошло достаточно времени
                requestAnimationFrame(() => {
                    drawMinimap();
                    lastMinimapDrawRef.current = performance.now();
                });
            }
            else {
                // Откладываем перерисовку
                if (minimapThrottleRef.current != null) {
                    clearTimeout(minimapThrottleRef.current);
                }
                minimapThrottleRef.current = window.setTimeout(() => {
                    requestAnimationFrame(() => {
                        drawMinimap();
                        lastMinimapDrawRef.current = performance.now();
                    });
                    minimapThrottleRef.current = null;
                }, MINIMAP_THROTTLE_MS - timeSinceLastDraw);
            }
        }
        // Запускаем таймер скрытия, если курсор не на миникарте
        if (!isHovered) {
            startHideTimeout();
        }
        return () => {
            clearHideTimeout();
            if (minimapThrottleRef.current != null) {
                clearTimeout(minimapThrottleRef.current);
            }
        };
    }, [stagePosition, zoom, isHovered, drawMinimap, startHideTimeout, clearHideTimeout]);
    // Перерисовываем миникарту при изменении элементов или видимости (с throttling)
    useEffect(() => {
        if (isVisible) {
            const now = performance.now();
            const timeSinceLastDraw = now - lastMinimapDrawRef.current;
            if (timeSinceLastDraw >= MINIMAP_THROTTLE_MS) {
                requestAnimationFrame(() => {
                    drawMinimap();
                    lastMinimapDrawRef.current = performance.now();
                });
            }
            else {
                if (minimapThrottleRef.current != null) {
                    clearTimeout(minimapThrottleRef.current);
                }
                minimapThrottleRef.current = window.setTimeout(() => {
                    requestAnimationFrame(() => {
                        drawMinimap();
                        lastMinimapDrawRef.current = performance.now();
                    });
                    minimapThrottleRef.current = null;
                }, MINIMAP_THROTTLE_MS - timeSinceLastDraw);
            }
        }
    }, [boardState.layers, isVisible, drawMinimap]);
    /**
     * Проверяет, находится ли точка в области миникарты
     */
    const isPointInMinimap = useCallback((clientX, clientY) => {
        if (!containerRef.current)
            return null;
        const rect = containerRef.current.getBoundingClientRect();
        const x = clientX - rect.left;
        const y = clientY - rect.top;
        // Проверяем попадание в границы миникарты
        if (x >= 0 && x <= MINIMAP_WIDTH && y >= 0 && y <= MINIMAP_HEIGHT) {
            return { x, y };
        }
        return null;
    }, []);
    /**
     * Обработчик клика по миникарте
     * Обрабатывается через document в capture phase, чтобы работать с pointerEvents: none
     */
    const handleDocumentClick = useCallback((e) => {
        if (!stageRef || !isVisible)
            return;
        const point = isPointInMinimap(e.clientX, e.clientY);
        if (!point)
            return;
        // Блокируем распространение события, чтобы не вызывать действия стейджа
        e.stopPropagation();
        e.preventDefault();
        // Преобразуем координаты клика в мировые координаты
        const worldPos = minimapToWorld(point.x, point.y);
        // Вычисляем новую позицию стейджа для центрирования на выбранной точке
        const viewportCenterX = window.innerWidth / 2;
        const viewportCenterY = window.innerHeight / 2;
        const newStagePosition = {
            x: viewportCenterX - worldPos.x * zoom,
            y: viewportCenterY - worldPos.y * zoom,
        };
        // Обновляем позицию стейджа
        stageRef.position(newStagePosition);
        setPosition(newStagePosition);
        previousPositionRef.current = newStagePosition;
    }, [stageRef, minimapToWorld, zoom, setPosition, isVisible, isPointInMinimap]);
    /**
     * Обработчик движения мыши для определения наведения на миникарту
     * Обновляет состояние hover и управляет таймером скрытия
     */
    const handleDocumentMouseMove = useCallback((e) => {
        if (!isVisible)
            return;
        const point = isPointInMinimap(e.clientX, e.clientY);
        const isInside = point !== null;
        if (isInside && !isHovered) {
            // Курсор вошел в область миникарты
            setIsHovered(true);
            clearHideTimeout();
        }
        else if (!isInside && isHovered) {
            // Курсор вышел из области миникарты
            setIsHovered(false);
            startHideTimeout();
        }
    }, [isVisible, isHovered, isPointInMinimap, clearHideTimeout, startHideTimeout]);
    // Подписываемся на события document, когда миникарта видима
    useEffect(() => {
        if (!isVisible)
            return;
        // Используем capture phase для перехвата событий до их попадания на другие элементы
        document.addEventListener('click', handleDocumentClick, true);
        document.addEventListener('mousemove', handleDocumentMouseMove);
        return () => {
            document.removeEventListener('click', handleDocumentClick, true);
            document.removeEventListener('mousemove', handleDocumentMouseMove);
        };
    }, [isVisible, handleDocumentClick, handleDocumentMouseMove]);
    return (_jsx("div", { ref: containerRef, style: Object.assign({ position: 'relative', width: MINIMAP_WIDTH, height: MINIMAP_HEIGHT, marginBottom: 10, padding: 0, pointerEvents: 'none', opacity: isVisible ? 0.8 : 0, transition: 'opacity 0.3s ease-in-out', borderRadius: 8, overflow: 'hidden' }, styles), "aria-label": "\u041C\u0438\u043D\u0438\u043A\u0430\u0440\u0442\u0430 \u0434\u043E\u0441\u043A\u0438", children: _jsx("canvas", { ref: canvasRef, width: MINIMAP_WIDTH, height: MINIMAP_HEIGHT, style: {
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
            }, "aria-label": "\u041C\u0438\u043D\u0438\u043A\u0430\u0440\u0442\u0430 \u0434\u043E\u0441\u043A\u0438. \u041A\u043B\u0438\u043A\u043D\u0438\u0442\u0435 \u0434\u043B\u044F \u043F\u0435\u0440\u0435\u0445\u043E\u0434\u0430 \u043A \u0442\u043E\u0447\u043A\u0435" }) }));
};
export default MiniMap;
